import { api } from "@/api/client";
import { type DiscoveryStart, type DiscoveryStartRow, PLACE_ROLES } from "@/api/types";
import { CountTable, rankCounts } from "@/components/CountTable";
import { ThumbDownIcon, ThumbUpIcon } from "@/components/Icons";
import { InterestTerms } from "@/components/InterestTerms";
import { InfoDot, PanelHelp } from "@/components/PanelHelp";
import { Staleness } from "@/components/Staleness";
import { useECharts } from "@/hooks/useECharts";
import { usePoll } from "@/hooks/usePoll";
import { t, type TranslationKey } from "@/i18n";
import {
  formatDateTime,
  formatDecimal,
  formatDurationSeconds,
  formatInt,
  formatPercent,
  modeLabel,
} from "@/lib/format";
import {
  affectLabel,
  intentLabel,
  placeRoleLabel,
  professionTopicLabel,
} from "@/lib/vocabulary";
import { includeTestSessions, selectedTenant, selectedWindow } from "@/state/filters";

// The places shown per role: the most named first.
const PLACE_LIMIT = 25;

/** «n Antworten auf Rückfragen nicht mitgezählt», one in the singular. */
const replyNote = (n: number): string =>
  n === 1 ? t("engagement.replyTurnsOne") : t("engagement.replyTurns", { n: formatInt(n) });

export function EngagementView() {
  const { data, loading, error, lastFetched, refetch } = usePoll(
    (s) =>
      api.engagement(
        {
          window: selectedWindow.value,
          includeTest: includeTestSessions.value,
          tenant: selectedTenant.value,
        },
        s,
      ),
    [selectedWindow.value, includeTestSessions.value, selectedTenant.value],
  );

  const turnsBucketOption = data
    ? {
        title: {
          text: t("engagement.chart.turnsPerSession"),
          left: "center",
          textStyle: { fontSize: 14 },
        },
        tooltip: { trigger: "axis" },
        xAxis: { type: "category", data: Object.keys(data.turns_per_session_histogram) },
        yAxis: { type: "value" },
        series: [
          {
            type: "bar",
            data: Object.values(data.turns_per_session_histogram),
            itemStyle: { color: "#002da5" },
          },
        ],
      }
    : null;
  const turnsBucketRef = useECharts(turnsBucketOption);

  const intentOption = data
    ? barChart(t("engagement.chart.intent"), data.intent_distribution, { label: intentLabel })
    : null;
  const intentRef = useECharts(intentOption);

  const sessionIntentOption = data
    ? barChart(t("engagement.chart.sessionIntent"), data.session_intent_distribution ?? {}, {
        label: intentLabel,
        color: "#fab400",
      })
    : null;
  const sessionIntentRef = useECharts(sessionIntentOption);

  const mentionOption = data
    ? barChart(t("engagement.chart.mentions"), data.mentions_distribution)
    : null;
  const mentionRef = useECharts(mentionOption);

  // No chart for a window without a Beruf conversation: nine bars at zero
  // would read as a result.
  const professionConversations = data?.profession_conversations ?? 0;
  const professionTopicsOption =
    data && professionConversations > 0
      ? barChart(t("engagement.chart.professionTopics"), data.profession_topics ?? {}, {
          label: professionTopicLabel,
        })
      : null;
  const professionTopicsRef = useECharts(professionTopicsOption);

  const berufsfeldOption = data
    ? barChart(t("engagement.chart.berufsfeld"), data.berufsfeld_distribution)
    : null;
  const berufsfeldRef = useECharts(berufsfeldOption);

  const affectOption = data
    ? barChart(t("engagement.chart.affect"), data.affect_distribution, { label: affectLabel })
    : null;
  const affectRef = useECharts(affectOption);

  const trendWindow = data?.interest_trend_window ?? "";
  const terms = data?.interest_terms ?? [];

  return (
    <div style="display: grid; gap: 24px;">
      <div style="display: flex; align-items: center; gap: 24px;">
        <Staleness
          lastFetched={lastFetched}
          loading={loading}
          error={error}
          onRefresh={refetch}
        />
      </div>

      <section class="card">
        <h2>{t("engagement.turnVolume")}</h2>
        <PanelHelp k="engagement.turnVolume" />
        <div class="grid grid-3">
          <div ref={turnsBucketRef} class="chart" />
          <div>
            <h3>{t("engagement.feedbackPerMode")}</h3>
            <table class="table">
              <thead>
                <tr>
                  <th>{t("common.mode")}</th>
                  <th>
                    <ThumbUpIcon label={t("common.ratingPositive")} />
                  </th>
                  <th>
                    <ThumbDownIcon label={t("common.ratingNegative")} />
                  </th>
                  <th>{t("engagement.ratio")}</th>
                </tr>
              </thead>
              <tbody>
                {data &&
                  Object.values(data.feedback_by_mode).map((fb) => (
                    <tr key={fb.mode}>
                      <td>{modeLabel(fb.mode)}</td>
                      <td>{formatInt(fb.positive)}</td>
                      <td>{formatInt(fb.negative)}</td>
                      <td>{fb.ratio == null ? "—" : formatPercent(fb.ratio)}</td>
                    </tr>
                  ))}
                {(!data || Object.keys(data.feedback_by_mode).length === 0) && (
                  <tr>
                    <td colspan={4} class="muted">
                      {t("engagement.noFeedback")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div>
            <h3>
              {t("engagement.bounceByMode")} <InfoDot k="engagement.bounceRate" />
            </h3>
            <table class="table">
              <thead>
                <tr>
                  <th>{t("common.mode")}</th>
                  <th>{t("engagement.bounce")}</th>
                </tr>
              </thead>
              <tbody>
                {data &&
                  Object.entries(data.bounce_rate_by_mode).map(([mode, rate]) => (
                    <tr key={mode}>
                      <td>{modeLabel(mode)}</td>
                      <td>{formatPercent(rate)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section class="card">
        <h2>{t("engagement.duration")}</h2>
        <table class="table">
          <thead>
            <tr>
              <th>{t("common.mode")}</th>
              <th>{t("engagement.chats")}</th>
              <th>{t("engagement.multiTurn")}</th>
              <th>{t("engagement.avgDuration")}</th>
              <th>{t("engagement.median")}</th>
              <th>{t("engagement.max")}</th>
            </tr>
          </thead>
          <tbody>
            {data?.duration_by_mode &&
              Object.values(data.duration_by_mode)
                .sort((a, b) => b.chats - a.chats)
                .map((d) => (
                  <tr key={d.mode}>
                    <td>{modeLabel(d.mode)}</td>
                    <td>{formatInt(d.chats)}</td>
                    <td>{formatInt(d.multi_turn_chats)}</td>
                    <td>
                      {d.avg_duration_seconds == null
                        ? "—"
                        : formatDurationSeconds(d.avg_duration_seconds)}
                    </td>
                    <td>
                      {d.median_duration_seconds == null
                        ? "—"
                        : formatDurationSeconds(d.median_duration_seconds)}
                    </td>
                    <td>
                      {d.max_duration_seconds == null
                        ? "—"
                        : formatDurationSeconds(d.max_duration_seconds)}
                    </td>
                  </tr>
                ))}
            {(!data?.duration_by_mode ||
              Object.keys(data.duration_by_mode).length === 0) && (
              <tr>
                <td colspan={6} class="muted">
                  {t("engagement.noChats")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div class="muted" style="margin-top: 8px; font-size: 12px;">
          {t("engagement.durationNote")}
        </div>
      </section>

      <section class="card">
        <h2>{t("engagement.discoveryStart")}</h2>
        <PanelHelp k="engagement.discoveryStart" />
        <DiscoveryStartTable rows={data?.discovery_start ?? []} />
      </section>

      <section class="card">
        <h2>{t("engagement.topics")}</h2>
        <PanelHelp k="engagement.topics" />
        <div class="grid grid-3">
          <div>
            <div ref={intentRef} class="chart" />
            {data?.reply_turns !== undefined && (
              <div class="kpi-sub">{replyNote(data.reply_turns)}</div>
            )}
          </div>
          <div ref={sessionIntentRef} class="chart" />
          <div ref={mentionRef} class="chart" />
          <div ref={berufsfeldRef} class="chart" />
        </div>
        <div class="muted" style="margin-top: 8px; font-size: 12px;">
          {t("engagement.topicsNote")}
        </div>
      </section>

      <section class="card">
        <h2>{t("engagement.terms")}</h2>
        <PanelHelp k="engagement.terms" />
        <InterestTerms
          title={t("engagement.terms")}
          rows={terms}
          baselineHead={
            trendWindow ? t("engagement.terms.baseline", { window: trendWindow }) : null
          }
          empty={t("engagement.terms.none")}
        />
        {data && !trendWindow && terms.length > 0 && (
          <div class="kpi-sub">{t("engagement.terms.noCompare")}</div>
        )}
      </section>

      <section class="card">
        <h2>{t("engagement.professionTopics")}</h2>
        <PanelHelp k="engagement.professionTopics" />
        <div ref={professionTopicsRef} class="chart" />
        {data && (
          <div class="kpi-sub">
            {professionConversations === 0
              ? t("engagement.professionTopics.none")
              : t("engagement.professionTopics.conversations", {
                  n: formatInt(professionConversations),
                })}
          </div>
        )}
      </section>

      <section class="card">
        <h2>{t("engagement.affect")}</h2>
        <PanelHelp k="engagement.affect" />
        <div ref={affectRef} class="chart" />
      </section>

      <section class="card">
        <h2>{t("engagement.places")}</h2>
        <PanelHelp k="engagement.places" />
        <div class="grid grid-3">
          {PLACE_ROLES.map((role) => (
            <CountTable
              key={role}
              title={placeRoleLabel(role)}
              counts={data?.place_roles?.[role] ?? {}}
              unit={t("place.col.conversations")}
              empty={t("engagement.places.none")}
              limit={PLACE_LIMIT}
            />
          ))}
        </div>
      </section>

      <section class="card">
        <h2>
          {t("engagement.interestVsChoice")} <InfoDot k="engagement.interestVsChoice" />
        </h2>
        <PanelHelp k="engagement.interestVsChoice" />
        <table class="table">
          <thead>
            <tr>
              <th>{t("engagement.interestTag")}</th>
              <th>{t("engagement.resolvedBerufsfeld")}</th>
              <th>{t("common.count")}</th>
            </tr>
          </thead>
          <tbody>
            {data?.interest_vs_choice_gap.slice(0, 30).map((g, i) => (
              <tr key={i}>
                <td>{g.interest}</td>
                <td>{g.berufsfeld}</td>
                <td>{formatInt(g.count)}</td>
              </tr>
            ))}
            {(!data || data.interest_vs_choice_gap.length === 0) && (
              <tr>
                <td colspan={3} class="muted">
                  {t("engagement.noGap")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <section class="card">
        <h2>{t("engagement.feedbackComments")}</h2>
        <PanelHelp k="engagement.feedbackComments" />
        <table class="table">
          <thead>
            <tr>
              <th>{t("common.when")}</th>
              <th>{t("common.mode")}</th>
              <th>
                <ThumbUpIcon label={t("common.ratingPositive")} />/
                <ThumbDownIcon label={t("common.ratingNegative")} />
              </th>
              <th>{t("engagement.comment")}</th>
            </tr>
          </thead>
          <tbody>
            {data?.feedback_comments.map((fc) => (
              <tr key={fc.turn_id}>
                <td class="muted">{formatDateTime(fc.timestamp)}</td>
                <td>{modeLabel(fc.mode)}</td>
                <td>
                  {fc.feedback === "positive" ? (
                    <ThumbUpIcon label={t("common.ratingPositive")} />
                  ) : (
                    <ThumbDownIcon label={t("common.ratingNegative")} />
                  )}
                </td>
                <td>{fc.comment}</td>
              </tr>
            ))}
            {(!data || data.feedback_comments.length === 0) && (
              <tr>
                <td colspan={4} class="muted">
                  {t("engagement.noComments")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}

const START_LABEL: Record<DiscoveryStart, TranslationKey> = {
  questions: "engagement.discoveryStart.questions",
  free_text: "engagement.discoveryStart.freeText",
};

/** Orientierung conversations by start: how many, their share, their depth and
 * their ratings, so the Schnellstart and free text compare on quality. */
export function DiscoveryStartTable({ rows }: { rows: readonly DiscoveryStartRow[] }) {
  const total = rows.reduce((sum, row) => sum + row.conversations, 0);
  return (
    <table class="table">
      <thead>
        <tr>
          <th>{t("engagement.discoveryStart.start")}</th>
          <th>{t("engagement.chats")}</th>
          <th>{t("engagement.discoveryStart.share")}</th>
          <th>{t("engagement.discoveryStart.turnsPer")}</th>
          <th>
            <ThumbUpIcon label={t("common.ratingPositive")} />
          </th>
          <th>
            <ThumbDownIcon label={t("common.ratingNegative")} />
          </th>
          <th>{t("engagement.ratio")}</th>
        </tr>
      </thead>
      <tbody>
        {total > 0 &&
          rows.map((row) => (
            <tr key={row.start}>
              <td>{t(START_LABEL[row.start])}</td>
              <td>{formatInt(row.conversations)}</td>
              <td>{formatPercent(row.conversations / total)}</td>
              <td>{row.conversations ? formatDecimal(row.turns / row.conversations) : "—"}</td>
              <td>{formatInt(row.positive)}</td>
              <td>{formatInt(row.negative)}</td>
              <td>{row.ratio == null ? "—" : formatPercent(row.ratio)}</td>
            </tr>
          ))}
        {total === 0 && (
          <tr>
            <td colspan={7} class="muted">
              {t("engagement.discoveryStart.none")}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

/** A bar chart of a distribution, the 25 largest first; `label` puts a key
 * the backend sends by id (an intent, an affect tag) in words. */
export function barChart(
  title: string,
  dist: Readonly<Record<string, number>>,
  { label = (key: string) => key, color = "#002da5" }: {
    label?: (key: string) => string;
    color?: string;
  } = {},
) {
  const entries = rankCounts(dist, 25);
  return {
    title: { text: title, left: "center", textStyle: { fontSize: 14 } },
    tooltip: { trigger: "axis" },
    xAxis: {
      type: "category",
      data: entries.map(([key]) => label(key)),
      axisLabel: { rotate: 35 },
    },
    yAxis: { type: "value" },
    series: [
      {
        type: "bar",
        data: entries.map(([, v]) => v),
        itemStyle: { color },
      },
    ],
  };
}
